import {Spec} from 'immutability-helper';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as navigation from 'Navigation';
import {EntryProtocol, ExternalProtocol, FromServerProtocol} from 'notebook/ELNConstants';
import TemplateFile from 'notebook/entry-templates/backbone/filestate/TemplateFile';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import notifications from 'notifications';
import * as popup from 'Popup';

import Entry from '../filestate/Entry';

interface BaseEntryProtocolTabStateDefaults extends navigation.TabStateDefaults {
  protocolCid: string | null;
  file: Entry | TemplateFile | null;
}

interface EntryProtocolTabAttributes {
  file: Entry | TemplateFile;
}

class BaseEntryProtocolTabState extends navigation.TabState {
  override defaults(): BaseEntryProtocolTabStateDefaults {
    return {
      ...super.defaults(),
      title: 'Protocol',
      closeable: true,
      protocolCid: null,
      file: null, // entry or template
      preferenceKey: navigation.PREFERENCE_KEY_IGNORE,
    };
  }

  override initialize(attrs: EntryProtocolTabAttributes, options?: {}): void {
    this.printHandler = this.printHandler.bind(this);
    // eslint-disable-next-line prefer-rest-params
    super.initialize(...arguments);

    switch (attrs.file.fileType()) {
      case ModelTypeConstants.ENTRY:
        this.set({printOptions: ['protocol', 'entry']});
        break;
      case ModelTypeConstants.TEMPLATE:
      case ModelTypeConstants.STAGE_ENTRY:
        this.set({printOptions: []}); // TODO(T8957): support print
        break;
      default:
        throw new Error('Unexpected file type');
    }
  }

  addProtocol(protocol: ExternalProtocol | FromServerProtocol): void {
    this.file().addProtocol(protocol);
  }

  updateProtocol(op: Spec<EntryProtocol>): void {
    this.file().updateProtocol(this.get('protocolCid'), op);
  }

  removeProtocol(): void {
    this.file().removeProtocol(this.get('protocolCid'));
  }

  revertProtocol(protocol: EntryProtocol, selectionData: SelectionRestoreData): void {
    this.file().revertProtocol(protocol, selectionData);
  }

  printHandler(type: 'entry' | 'protocol'): void {
    assert(this.file().fileType() === ModelTypeConstants.ENTRY, 'Unexpected file type');
    switch (type) {
      case 'entry':
        EntryActions.print(this.file().id, {showNote: true});
        break;
      case 'protocol': {
        const protocol = this.protocol();
        if (protocol?.externalFile) {
          notifications.info('To print this protocol, please download the file.');
          return;
        }
        EntryActions.print(this.file().id, {showNote: false, protocolCids: [protocol!.cid]});
        break;
      }
      default:
        throw new Error(`Unexpected print handler type ${type}`);
    }
  }

  printOptions(): Array<'entry' | 'protocol'> {
    return this.get('printOptions');
  }

  protocol(): EntryProtocol | undefined {
    return _.findWhere(this.file().entryProtocols(), {cid: this.get('protocolCid')});
  }

  file(): Entry | TemplateFile {
    return this.get('file');
  }

  override close(): void {
    new popup.Confirm({
      title: 'Remove protocol?',
      msg: `Are you sure you want to remove this protocol from the entry? Any changes you have made \
will be lost.`,
    })
      .choice('Remove Protocol', 'btn-af-danger', () => {
        this.removeProtocol();
      })
      .choice('Cancel', 'btn-af-default')
      .show();
  }
}

export default BaseEntryProtocolTabState;
